import { useState, useRef, useEffect } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { BugReport as BugReportIcon } from '@mui/icons-material';
import { observer } from 'mobx-react-lite';
import { useStore } from 'logic';
import { RouteNames } from 'logic/routes';
import { designTokens } from 'theme';
import { FONT_THEMES, MODULE_INDICATOR_OPTIONS } from 'logic/ui-theme';
import { SnackbarPosition } from '@creative-kit/shared';
import type { SessionOwnershipTransferCandidate } from 'logic/sessions';
import { ReportBugModal } from 'ui/ReportBugModal';

const TOAST_POSITION_OPTIONS: { id: SnackbarPosition; name: string }[] = [
  { id: 'bottom-right', name: 'Bottom Right' },
  { id: 'bottom-center', name: 'Bottom Center' },
  { id: 'bottom-left', name: 'Bottom Left' },
  { id: 'top-center', name: 'Top Center' },
  { id: 'top-right', name: 'Top Right' },
];

export const AvatarMenu = observer(() => {
  const {
    authStore: { user, logout },
    routerStore,
    sessionsStore: {
      currentSession,
      getSessionOwnershipTransferCandidates,
      transferSessionOwnership,
    },
    uiFilenameStore: { openDialog: openFilenameDialog },
    uiCardTitleStore: { openDialog: openCardTitleDialog },
    uiThemeStore: {
      selectedFontThemeId,
      setFontTheme,
      moduleIndicatorStyle,
      setModuleIndicatorStyle,
    },
    uiSnackbarStore: { snackbarPosition, setSnackbarDetails },
    userSettingsStore: { modifyUserSettings },
    uiInfoPanelStore,
  } = useStore();

  const hasOrganization = user?.hasOrganization || false;
  const isCQAdmin = user?.isCQAdmin || false;

  const [isOpen, setIsOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<'appearance' | 'session' | null>(null);
  const [reportBugModalOpen, setReportBugModalOpen] = useState(false);
  const [ownershipTransferOpen, setOwnershipTransferOpen] = useState(false);
  const [ownershipCandidates, setOwnershipCandidates] = useState<
    SessionOwnershipTransferCandidate[]
  >([]);
  const [ownershipCandidatesLoading, setOwnershipCandidatesLoading] = useState(false);
  const [ownershipSelected, setOwnershipSelected] =
    useState<SessionOwnershipTransferCandidate | null>(null);
  const [ownershipSubmitting, setOwnershipSubmitting] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Toggle submenu - accordion style (only one open at a time)
  const toggleSubmenu = (submenu: 'appearance' | 'session') => {
    setOpenSubmenu((current) => (current === submenu ? null : submenu));
  };

  // Get user's initials for avatar (2 initials from display name or email)
  const getUserInitials = () => {
    const name = user?.displayName || user?.email || 'User';
    const words = name.split(/[\s@]+/).filter(Boolean); // Split on spaces or @ (for emails)
    if (words.length >= 2) {
      return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };
  const userInitials = getUserInitials();

  // Close menu when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    // Delay listener to prevent immediate close on the same click that opened menu
    const frameId = requestAnimationFrame(() => {
      document.addEventListener('click', handleClickOutside);
    });

    return () => {
      cancelAnimationFrame(frameId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen]);

  // Close on escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (!ownershipTransferOpen || !currentSession?.id) return;
    let cancelled = false;
    setOwnershipCandidatesLoading(true);
    setOwnershipSelected(null);
    getSessionOwnershipTransferCandidates(currentSession.id)
      .then((list) => {
        if (!cancelled) setOwnershipCandidates(list);
      })
      .catch(() => {
        if (!cancelled) {
          setOwnershipCandidates([]);
          setSnackbarDetails({
            description: 'Could not load users eligible for ownership transfer.',
          });
        }
      })
      .finally(() => {
        if (!cancelled) setOwnershipCandidatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    ownershipTransferOpen,
    currentSession?.id,
    getSessionOwnershipTransferCandidates,
    setSnackbarDetails,
  ]);

  const handleConfirmOwnershipTransfer = async () => {
    if (!currentSession || !ownershipSelected) return;
    setOwnershipSubmitting(true);
    try {
      await transferSessionOwnership(currentSession.id, ownershipSelected.userId);
      setSnackbarDetails({ description: 'Ownership transferred successfully.' });
      setOwnershipTransferOpen(false);
      setOwnershipSelected(null);
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : 'Could not transfer ownership.';
      setSnackbarDetails({ description: message });
    } finally {
      setOwnershipSubmitting(false);
    }
  };

  const handleSignOut = () => {
    setIsOpen(false);
    logout();
  };

  const handleOrganizationSettings = () => {
    setIsOpen(false);
    routerStore.goTo(RouteNames.ORGANIZATION_ADMIN);
  };

  const handleCQAdminSettings = () => {
    setIsOpen(false);
    routerStore.goTo(RouteNames.CQ_ADMIN);
  };

  const handleAbout = () => {
    setIsOpen(false);
    uiInfoPanelStore.open();
  };

  const handleReportBug = () => {
    setIsOpen(false);
    setReportBugModalOpen(true);
  };

  return (
    <Box ref={menuRef} sx={{ position: 'relative' }}>
      {/* Avatar button */}
      <Box
        component="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="User menu"
        aria-expanded={isOpen}
        aria-haspopup="true"
        sx={{
          width: 36,
          height: 36,
          background: 'linear-gradient(135deg, #ff3c3c, #ff6b6b)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: designTokens.typography.fontSize.sm,
          fontWeight: designTokens.typography.fontWeight.bold,
          color: designTokens.colors.text.primary,
          cursor: 'pointer',
          border: 'none',
          transition: `transform ${designTokens.transitions.normal}`,
          fontFamily: designTokens.typography.fontMono,
          '&:hover': {
            transform: 'scale(1.05)',
          },
        }}
      >
        {userInitials}
      </Box>

      {/* Dropdown menu */}
      <Box
        sx={{
          position: 'absolute',
          top: 'calc(100% + 8px)',
          right: 0,
          bgcolor: designTokens.menu.bg,
          border: `1px solid ${designTokens.menu.border}`,
          borderRadius: designTokens.menu.radius,
          boxShadow: designTokens.menu.shadow,
          backdropFilter: designTokens.menu.blur,
          padding: '8px',
          minWidth: 200,
          maxHeight: 'calc(100vh - 100px)',
          overflowY: 'auto',
          zIndex: 100,
          opacity: isOpen ? 1 : 0,
          visibility: isOpen ? 'visible' : 'hidden',
          transform: isOpen ? 'translateY(0)' : 'translateY(-8px)',
          transition: `all ${designTokens.transitions.normal}`,
          fontFamily: designTokens.typography.fontMono,
        }}
      >
        {/* User info section */}
        <Box
          sx={{
            padding: '12px',
            borderBottom: `1px solid ${designTokens.colors.border.subtle}`,
            mb: 1,
          }}
        >
          <Box
            sx={{
              fontSize: designTokens.typography.fontSize.sm,
              fontWeight: designTokens.typography.fontWeight.bold,
              color: designTokens.colors.text.primary,
              mb: 0.5,
            }}
          >
            {user?.displayName || 'User'}
          </Box>
          <Box
            sx={{
              fontSize: designTokens.typography.fontSize.xs,
              color: designTokens.colors.text.muted,
            }}
          >
            {user?.email}
          </Box>
        </Box>

        {/* Appearance section */}
        <Box sx={{ position: 'relative' }}>
          <MenuItem onClick={() => toggleSubmenu('appearance')}>
            <AppearanceIcon />
            Appearance
            <ChevronRightIcon isOpen={openSubmenu === 'appearance'} />
          </MenuItem>

          {/* Font themes submenu - minimal design */}
          <Box
            sx={{
              overflow: 'hidden',
              maxHeight: openSubmenu === 'appearance' ? 600 : 0,
              transition: 'max-height 0.2s ease-out',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
                py: 0.5,
                px: 1,
              }}
            >
              {/* Font themes section */}
              <Box
                sx={{
                  fontSize: designTokens.typography.fontSize.xs,
                  color: designTokens.colors.text.dim,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  px: 1,
                  py: 0.5,
                }}
              >
                Font
              </Box>
              {FONT_THEMES.map((theme) => {
                const isSelected = selectedFontThemeId === theme.id;
                return (
                  <Box
                    key={theme.id}
                    component="button"
                    onClick={() => setFontTheme(theme.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      width: '100%',
                      padding: '10px 12px',
                      bgcolor: isSelected ? designTokens.colors.bg.buttonHover : 'transparent',
                      border: 'none',
                      borderRadius: designTokens.spacing.radiusSm,
                      cursor: 'pointer',
                      transition: `all ${designTokens.transitions.fast}`,
                      textAlign: 'left',
                      '&:hover': {
                        bgcolor: designTokens.colors.bg.buttonHover,
                      },
                    }}
                  >
                    {/* Radio indicator */}
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        border: `2px solid ${
                          isSelected
                            ? designTokens.colors.text.primary
                            : designTokens.colors.text.dim
                        }`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        transition: `all ${designTokens.transitions.fast}`,
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: isSelected ? designTokens.colors.text.primary : 'transparent',
                          transition: `all ${designTokens.transitions.fast}`,
                        }}
                      />
                    </Box>
                    {/* Font name in its own typeface */}
                    <Box
                      sx={{
                        fontFamily: theme.editorial,
                        fontSize: designTokens.typography.fontSize.sm,
                        fontWeight: isSelected
                          ? designTokens.typography.fontWeight.bold
                          : designTokens.typography.fontWeight.normal,
                        color: isSelected
                          ? designTokens.colors.text.primary
                          : designTokens.colors.text.secondary,
                        transition: `all ${designTokens.transitions.fast}`,
                      }}
                    >
                      {theme.name}
                    </Box>
                  </Box>
                );
              })}

              {/* Module indicators section */}
              <Box sx={{ height: 1, bgcolor: designTokens.colors.border.subtle, my: 1 }} />
              <Box
                sx={{
                  fontSize: designTokens.typography.fontSize.xs,
                  color: designTokens.colors.text.dim,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  px: 1,
                  py: 0.5,
                }}
              >
                Module Labels
              </Box>
              {MODULE_INDICATOR_OPTIONS.map((option) => {
                const isSelected = moduleIndicatorStyle === option.id;
                return (
                  <Box
                    key={option.id}
                    component="button"
                    onClick={() => setModuleIndicatorStyle(option.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      width: '100%',
                      padding: '10px 12px',
                      bgcolor: isSelected ? designTokens.colors.bg.buttonHover : 'transparent',
                      border: 'none',
                      borderRadius: designTokens.spacing.radiusSm,
                      cursor: 'pointer',
                      transition: `all ${designTokens.transitions.fast}`,
                      textAlign: 'left',
                      '&:hover': {
                        bgcolor: designTokens.colors.bg.buttonHover,
                      },
                    }}
                  >
                    {/* Radio indicator */}
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        border: `2px solid ${
                          isSelected
                            ? designTokens.colors.text.primary
                            : designTokens.colors.text.dim
                        }`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        transition: `all ${designTokens.transitions.fast}`,
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: isSelected ? designTokens.colors.text.primary : 'transparent',
                          transition: `all ${designTokens.transitions.fast}`,
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        fontSize: designTokens.typography.fontSize.sm,
                        fontWeight: isSelected
                          ? designTokens.typography.fontWeight.bold
                          : designTokens.typography.fontWeight.normal,
                        color: isSelected
                          ? designTokens.colors.text.primary
                          : designTokens.colors.text.secondary,
                        transition: `all ${designTokens.transitions.fast}`,
                      }}
                    >
                      {option.name}
                    </Box>
                  </Box>
                );
              })}

              {/* Toast Position section */}
              <Box sx={{ height: 1, bgcolor: designTokens.colors.border.subtle, my: 1 }} />
              <Box
                sx={{
                  fontSize: designTokens.typography.fontSize.xs,
                  color: designTokens.colors.text.dim,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  px: 1,
                  py: 0.5,
                }}
              >
                Toast Position
              </Box>
              {TOAST_POSITION_OPTIONS.map((option) => {
                const isSelected = snackbarPosition === option.id;
                return (
                  <Box
                    key={option.id}
                    component="button"
                    onClick={() => modifyUserSettings({ snackbarPosition: option.id })}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      width: '100%',
                      padding: '10px 12px',
                      bgcolor: isSelected ? designTokens.colors.bg.buttonHover : 'transparent',
                      border: 'none',
                      borderRadius: designTokens.spacing.radiusSm,
                      cursor: 'pointer',
                      transition: `all ${designTokens.transitions.fast}`,
                      textAlign: 'left',
                      '&:hover': {
                        bgcolor: designTokens.colors.bg.buttonHover,
                      },
                    }}
                  >
                    {/* Radio indicator */}
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        border: `2px solid ${
                          isSelected
                            ? designTokens.colors.text.primary
                            : designTokens.colors.text.dim
                        }`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        transition: `all ${designTokens.transitions.fast}`,
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: isSelected ? designTokens.colors.text.primary : 'transparent',
                          transition: `all ${designTokens.transitions.fast}`,
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        fontSize: designTokens.typography.fontSize.sm,
                        fontWeight: isSelected
                          ? designTokens.typography.fontWeight.bold
                          : designTokens.typography.fontWeight.normal,
                        color: isSelected
                          ? designTokens.colors.text.primary
                          : designTokens.colors.text.secondary,
                        transition: `all ${designTokens.transitions.fast}`,
                      }}
                    >
                      {option.name}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>

        {/* Session Settings section - only show when a session is active */}
        {currentSession && (
          <Box sx={{ position: 'relative' }}>
            <MenuItem onClick={() => toggleSubmenu('session')}>
              <SessionSettingsIcon />
              Session Settings
              <ChevronRightIcon isOpen={openSubmenu === 'session'} />
            </MenuItem>

            {/* Session Settings submenu */}
            <Box
              sx={{
                overflow: 'hidden',
                maxHeight: openSubmenu === 'session' ? 520 : 0,
                transition: 'max-height 0.2s ease-out',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px',
                  py: 0.5,
                  px: 1,
                }}
              >
                {/* Session name header */}
                <Box
                  sx={{
                    fontSize: designTokens.typography.fontSize.xs,
                    color: designTokens.colors.text.dim,
                    px: 1,
                    py: 0.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Settings for: {currentSession.data.name || 'Untitled Session'}
                </Box>

                <Box sx={{ height: 1, bgcolor: designTokens.colors.border.subtle, my: 0.5 }} />

                {/* Title Settings button */}
                <Box
                  component="button"
                  onClick={() => {
                    setIsOpen(false);
                    openCardTitleDialog();
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    width: '100%',
                    padding: '10px 12px',
                    bgcolor: 'transparent',
                    border: 'none',
                    borderRadius: designTokens.spacing.radiusSm,
                    cursor: 'pointer',
                    transition: `all ${designTokens.transitions.fast}`,
                    textAlign: 'left',
                    fontSize: designTokens.typography.fontSize.sm,
                    color: designTokens.colors.text.secondary,
                    '&:hover': {
                      bgcolor: designTokens.menu.itemHover,
                      color: designTokens.colors.text.primary,
                    },
                  }}
                >
                  Title Settings
                </Box>

                {/* Filename Settings button */}
                <Box
                  component="button"
                  onClick={() => {
                    setIsOpen(false);
                    openFilenameDialog();
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    width: '100%',
                    padding: '10px 12px',
                    bgcolor: 'transparent',
                    border: 'none',
                    borderRadius: designTokens.spacing.radiusSm,
                    cursor: 'pointer',
                    transition: `all ${designTokens.transitions.fast}`,
                    textAlign: 'left',
                    fontSize: designTokens.typography.fontSize.sm,
                    color: designTokens.colors.text.secondary,
                    '&:hover': {
                      bgcolor: designTokens.menu.itemHover,
                      color: designTokens.colors.text.primary,
                    },
                  }}
                >
                  Filename Settings
                </Box>

                {currentSession && user?.uid && currentSession.isUserOwner(user.uid) && (
                  <Box sx={{ px: 1, py: 0.5 }}>
                    <Box
                      sx={{
                        fontSize: designTokens.typography.fontSize.xs,
                        color: designTokens.colors.text.dim,
                        px: 1,
                        py: 0.5,
                      }}
                    >
                      Owner:{' '}
                      {user.displayName
                        ? user.email
                          ? `${user.displayName} (${user.email})`
                          : user.displayName
                        : user.email || 'You'}
                    </Box>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => {
                        setIsOpen(false);
                        setOwnershipTransferOpen(true);
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        width: '100%',
                        padding: '10px 12px',
                        bgcolor: 'transparent',
                        border: 'none',
                        borderRadius: designTokens.spacing.radiusSm,
                        cursor: 'pointer',
                        transition: `all ${designTokens.transitions.fast}`,
                        textAlign: 'left',
                        fontSize: designTokens.typography.fontSize.sm,
                        color: designTokens.colors.text.secondary,
                        '&:hover': {
                          bgcolor: designTokens.menu.itemHover,
                          color: designTokens.colors.text.primary,
                        },
                      }}
                    >
                      Transfer ownership…
                    </Box>
                  </Box>
                )}

                <Box sx={{ height: 1, bgcolor: designTokens.colors.border.subtle, my: 0.5 }} />

                {/* Toggle: Create Buckets For New Sources */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '6px 12px',
                  }}
                >
                  <Box
                    sx={{
                      fontSize: designTokens.typography.fontSize.sm,
                      color: designTokens.colors.text.secondary,
                    }}
                  >
                    Auto-add modules
                  </Box>
                  <Switch
                    size="small"
                    checked={currentSession.data.automaticModuleCreation || false}
                    onChange={() => currentSession.toggleAutomaticModuleCreation()}
                  />
                </Box>

                {/* Toggle: Auto-increment Card Title */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '6px 12px',
                  }}
                >
                  <Box
                    sx={{
                      fontSize: designTokens.typography.fontSize.sm,
                      color: designTokens.colors.text.secondary,
                    }}
                  >
                    Number card copies
                  </Box>
                  <Switch
                    size="small"
                    checked={currentSession.automaticCardTitleSuffix}
                    onChange={() => currentSession.toggleAutomaticCardTitleSuffix()}
                  />
                </Box>

                {/* Toggle: Auto-increment Filename */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '6px 12px',
                  }}
                >
                  <Box
                    sx={{
                      fontSize: designTokens.typography.fontSize.sm,
                      color: designTokens.colors.text.secondary,
                    }}
                  >
                    Number file copies
                  </Box>
                  <Switch
                    size="small"
                    checked={currentSession.automaticFilenameVersioning}
                    onChange={() => currentSession.toggleAutomaticFilenameVersioning()}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        <Box sx={{ height: 1, bgcolor: designTokens.colors.border.subtle, my: 1 }} />

        {/* Menu items */}
        {hasOrganization && (
          <MenuItem onClick={handleOrganizationSettings}>
            <SettingsIcon />
            Organization Settings
          </MenuItem>
        )}

        {isCQAdmin && (
          <MenuItem onClick={handleCQAdminSettings}>
            <AdminIcon />
            CQ Admin
          </MenuItem>
        )}

        <MenuItem onClick={handleAbout}>
          <AboutIcon />
          About Creative Queue
        </MenuItem>

        <MenuItem onClick={handleReportBug}>
          <BugReportIcon sx={{ fontSize: 16, opacity: 0.7 }} />
          Report Bug
        </MenuItem>

        <Box sx={{ height: 1, bgcolor: designTokens.colors.border.subtle, my: 1 }} />

        <MenuItem onClick={handleSignOut} danger>
          <SignOutIcon />
          Sign Out
        </MenuItem>
      </Box>

      <ReportBugModal open={reportBugModalOpen} onClose={() => setReportBugModalOpen(false)} />

      <Dialog
        open={ownershipTransferOpen}
        onClose={() => !ownershipSubmitting && setOwnershipTransferOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Transfer ownership of &quot;{currentSession?.data.name || 'Untitled Session'}&quot;
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The new owner will have full control of this project, including the ability to delete
            it.
          </Typography>
          {ownershipCandidatesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <>
              <Autocomplete
                options={ownershipCandidates}
                getOptionLabel={(option) =>
                  option.email ? `${option.name} (${option.email})` : option.name
                }
                isOptionEqualToValue={(option, value) => option.userId === value?.userId}
                value={ownershipSelected}
                onChange={(_e, newValue) => setOwnershipSelected(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="New owner"
                    placeholder="Select a collaborator"
                    variant="outlined"
                  />
                )}
              />
              {ownershipCandidates.length === 0 && !ownershipCandidatesLoading && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  No eligible users yet. Share the project with edit access or enable
                  organization-wide edit so teammates can receive ownership.
                </Typography>
              )}
              {ownershipSelected && user && (
                <Typography variant="body2" sx={{ mt: 2 }}>
                  <strong>{user.displayName || user.email || 'You'}</strong> will remain as an EDIT
                  collaborator. <strong>{ownershipSelected.name}</strong> will become the owner.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOwnershipTransferOpen(false)} disabled={ownershipSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleConfirmOwnershipTransfer()}
            disabled={
              !ownershipSelected ||
              ownershipSubmitting ||
              ownershipCandidatesLoading ||
              !currentSession
            }
          >
            {ownershipSubmitting ? 'Transferring…' : 'Transfer ownership'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

// Menu item component
interface MenuItemProps {
  children: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
}

const MenuItem = ({ children, onClick, danger }: MenuItemProps) => (
  <Box
    component="button"
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      width: '100%',
      padding: '10px 12px',
      bgcolor: 'transparent',
      border: 'none',
      borderRadius: designTokens.spacing.radiusSm,
      fontFamily: designTokens.typography.fontMono,
      fontSize: designTokens.typography.fontSize.xs,
      color: danger ? designTokens.colors.destructive.default : designTokens.colors.text.secondary,
      cursor: 'pointer',
      transition: `all ${designTokens.transitions.fast}`,
      textAlign: 'left',
      '&:hover': {
        bgcolor: danger ? designTokens.colors.destructive.bg : designTokens.menu.itemHover,
        color: danger ? designTokens.colors.destructive.hover : designTokens.colors.text.primary,
      },
    }}
  >
    {children}
  </Box>
);

// Settings icon
const SettingsIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 16, height: 16, opacity: 0.7 }}
  >
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

// Sign out icon
const SignOutIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 16, height: 16, opacity: 0.7 }}
  >
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

// Admin icon
const AdminIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 16, height: 16, opacity: 0.7 }}
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

// Session settings icon (sliders)
const SessionSettingsIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 16, height: 16, opacity: 0.7 }}
  >
    <line x1="4" y1="21" x2="4" y2="14" />
    <line x1="4" y1="10" x2="4" y2="3" />
    <line x1="12" y1="21" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12" y2="3" />
    <line x1="20" y1="21" x2="20" y2="16" />
    <line x1="20" y1="12" x2="20" y2="3" />
    <line x1="1" y1="14" x2="7" y2="14" />
    <line x1="9" y1="8" x2="15" y2="8" />
    <line x1="17" y1="16" x2="23" y2="16" />
  </svg>
);

// Appearance icon (palette)
const AppearanceIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 16, height: 16, opacity: 0.7 }}
  >
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="8" r="1.5" fill="currentColor" />
    <circle cx="8" cy="12" r="1.5" fill="currentColor" />
    <circle cx="16" cy="12" r="1.5" fill="currentColor" />
    <circle cx="12" cy="16" r="1.5" fill="currentColor" />
  </svg>
);

// About icon (info circle)
const AboutIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 16, height: 16, opacity: 0.7 }}
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

// Chevron right icon (for submenu)
const ChevronRightIcon = ({ isOpen }: { isOpen: boolean }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      width: 14,
      height: 14,
      opacity: 0.5,
      marginLeft: 'auto',
      transition: 'transform 0.2s ease',
      transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
    }}
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
